import { useEffect, useState } from 'react'
import { useGameBusValue } from '../hooks/useGameBus'
import { GameBus } from '@game/core/GameBus'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import SettingsModal from './SettingsModal'
import PauseOverlay from './PauseOverlay'
import './RetroHUD.css'

export default function RetroHUD() {
  const { progress } = useAuth()
  const navigate = useNavigate()

  // Sincronização dos estados do Jogador via GameBus
  const hp = useGameBusValue('hp:changed', { hp: 100, maxHp: 100 })
  const guard = useGameBusValue('guard:changed', { guard: 100, maxGuard: 100 })
  const axe = useGameBusValue('axe:changed', { axe: 0, maxAxe: 100 })
  const combo = useGameBusValue('combo:changed', { count: 0 })
  const heal = useGameBusValue('player:healUsed', { charges: 0 })
  const timeInfo = useGameBusValue('time:changed', { elapsedSec: 0 })

  // Pause e Configurações
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  // Estado do Boss ativo no Boss Rush
  const [bossInfo, setBossInfo] = useState<{
    id: string
    name: string
    hp: number
    maxHp: number
  }>({
    id: 'saci',
    name: 'Saci-Pererê',
    hp: 120,
    maxHp: 120,
  })

  // Sincronização com o Boss Rush Scene
  useEffect(() => {
    // Escuta quando o próximo boss é spawnado
    const offNext = GameBus.on('boss_rush:next', (data: { id: string; name: string; hp: number; maxHp: number }) => {
      setBossInfo({
        id: data.id,
        name: data.name,
        hp: data.hp,
        maxHp: data.maxHp,
      })
    })

    // Escuta as alterações de HP do inimigo atual
    const offHp = GameBus.on('enemy:hp:changed', (data: { hp: number; maxHp: number; name: string }) => {
      setBossInfo((prev) => {
        // Apenas atualiza se for o mesmo boss (ou assume o boss ativo se coincidir)
        return {
          ...prev,
          hp: Math.max(0, data.hp),
          maxHp: data.maxHp || prev.maxHp,
        }
      })
    })

    return () => {
      offNext()
      offHp()
    }
  }, [])

  // Sincronização de teclado para pause
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key.toLowerCase() === 'p') {
        if (isSettingsOpen) {
          setIsSettingsOpen(false)
        } else {
          togglePause()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPaused, isSettingsOpen])

  const togglePause = () => {
    const newPaused = !isPaused
    setIsPaused(newPaused)
    GameBus.emit(newPaused ? 'pause:request' : 'resume:request', undefined)
  }

  // Formatação do Tempo retrô
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0')
    const s = (secs % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  // Mapeamento da skin para o arquivo de retrato correspondente
  const getPlayerPortrait = () => {
    const skinKey = (progress?.skin as string) ?? 'negro'
    if (skinKey === 'raiz' || skinKey === 'negro') return '/assets/ui/skin_negro.png'
    if (skinKey === 'dourado' || skinKey === 'loiro') return '/assets/ui/skin_loiro.png'
    if (skinKey === 'equilibrado') return '/assets/ui/skin_equilibrado.png'
    return '/assets/ui/skin_moreno.png'
  }

  // Formata o nome do capoeirista
  const getPlayerName = () => {
    const skinKey = (progress?.skin as string) ?? 'negro'
    if (skinKey === 'raiz' || skinKey === 'negro') return 'RAIZ'
    if (skinKey === 'dourado' || skinKey === 'loiro') return 'DOURADO'
    if (skinKey === 'equilibrado' || skinKey === 'moreno') return 'EQUILIBRADO'
    return 'AURORA'
  }

  // Cálculos de porcentagem de vida
  const playerHpPct = Math.max(0, Math.min(100, (hp.hp / hp.maxHp) * 100))
  const bossHpPct = Math.max(0, Math.min(100, (bossInfo.hp / bossInfo.maxHp) * 100))
  const playerAxePct = Math.max(0, Math.min(100, (axe.axe / axe.maxAxe) * 100))
  const playerGuardPct = Math.max(0, Math.min(100, (guard.guard / guard.maxGuard) * 100))

  const isLowHp = hp.hp <= hp.maxHp * 0.3
  const isAxeMax = axe.axe >= axe.maxAxe

  return (
    <div className="retro-hud-container font-retro">
      {isLowHp && <div className="retro-danger-vignette" />}

      {/* HEADER PRINCIPAL ARCADE */}
      <div className="retro-header-row">
        {/* LADO DO JOGADOR */}
        <div className="retro-hud-section retro-player-section">
          <div className="retro-portrait-container">
            <img
              src={getPlayerPortrait()}
              alt="Player Portrait"
              className="retro-portrait-img retro-pixelated"
            />
          </div>
          <div className="retro-bars-container">
            <div className="retro-name-row">
              <span className="retro-role-label">1P</span>
              <span className="retro-name-label">{getPlayerName()}</span>
            </div>
            
            {/* Barra de HP do Jogador */}
            <div className="retro-bar-outer hp-bar-outer">
              <div 
                className={`retro-bar-inner hp-bar-inner ${isLowHp ? 'retro-bar-danger-flash' : ''}`}
                style={{ width: `${playerHpPct}%` }}
              />
              <span className="retro-bar-percentage-label">
                {Math.round(playerHpPct)}%
              </span>
            </div>

            {/* Barra de Axé (SP) do Jogador */}
            <div className="retro-bar-outer sp-bar-outer">
              <div 
                className={`retro-bar-inner sp-bar-inner ${isAxeMax ? 'retro-bar-sp-glow' : ''}`}
                style={{ width: `${playerAxePct}%` }}
              />
              <span className="retro-sp-label">SP</span>
            </div>

            {/* Barra de Guarda (GD) do Jogador */}
            <div className="retro-bar-outer sp-bar-outer" style={{ marginTop: '4px' }}>
              <div 
                className={`retro-bar-inner ${guard.guard <= 25 ? 'retro-bar-danger-flash' : ''}`}
                style={{ width: `${playerGuardPct}%`, background: guard.guard <= 25 ? '#ff3333' : '#3399ff' }}
              />
              <span className="retro-sp-label" style={{ color: guard.guard <= 25 ? '#ff3333' : '#3399ff' }}>GD</span>
            </div>
          </div>
        </div>

        {/* LOGO CENTRAL E CRONÔMETRO */}
        <div className="retro-center-section">
          <div className="retro-logo-container">
            <span className="retro-crest-icon">🛡️</span>
            <div className="retro-logo-text">CAPOEIRA SOUL</div>
          </div>
          <div className="retro-chronometer-container">
            <span className="retro-clock-icon">⏰</span>
            <span className="retro-clock-time">{formatTime(timeInfo.elapsedSec)}</span>
          </div>
        </div>

        {/* LADO DO CHEFE */}
        <div className="retro-hud-section retro-boss-section">
          <div className="retro-bars-container text-right">
            <div className="retro-name-row flex-reverse">
              <span className="retro-role-label label-boss">BOSS</span>
              <span className="retro-name-label">{bossInfo.name.toUpperCase()}</span>
            </div>
            
            {/* Barra de HP do Boss */}
            <div className="retro-bar-outer hp-bar-outer">
              <div 
                className="retro-bar-inner hp-bar-inner boss-hp-bar"
                style={{ width: `${bossHpPct}%` }}
              />
              <span className="retro-bar-percentage-label label-left">
                {Math.round(bossHpPct)}%
              </span>
            </div>
            
            <div className="retro-boss-details">
              <span>HP {Math.round(bossInfo.hp)} / {bossInfo.maxHp}</span>
            </div>
          </div>
          <div className="retro-portrait-container boss-portrait">
            <img
              src={`/assets/sprites/enemy_${bossInfo.id}_idle.png`}
              alt={bossInfo.name}
              className="retro-portrait-img boss-portrait-img retro-pixelated"
              onError={(e) => {
                // Fallback para sprite genérico se o específico não carregar
                ;(e.currentTarget as HTMLImageElement).src = '/assets/sprites/enemy_idle.png'
              }}
            />
          </div>
        </div>
      </div>

      {/* CONTROLE DE AÇÕES DO PAUSE E COMBOS */}
      <div className="retro-bottom-row">
        {/* Cargas de Cura */}
        {heal.charges > 0 && (
          <div className="retro-heal-display animate-float">
            <span className="heart-icon">✚</span> {heal.charges} {heal.charges === 1 ? 'CARGA' : 'CARGAS'}
          </div>
        )}

        {/* Exibição de Combos */}
        {combo.count > 1 && (
          <div key={combo.count} className="retro-combo-announcer animate-wobble">
            {combo.count} COMBOS!
          </div>
        )}

        {/* Botão de Pause Retro */}
        <button 
          className="retro-pause-btn"
          onClick={togglePause}
          aria-label={isPaused ? "Retomar Jogo" : "Pausar Jogo"}
        >
          {isPaused ? 'INSERT COIN / ▶' : 'PAUSE / ||'}
        </button>
      </div>

      {/* Overlays de Pause e Settings */}
      <PauseOverlay 
        isOpen={isPaused} 
        onResume={togglePause}
        onExit={() => navigate('/menu')}
        onSettings={() => setIsSettingsOpen(true)}
        skin={progress?.skin}
      />

      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        healCharges={heal.charges}
      />
    </div>
  )
}
